import store from "../../../store";
import cacheImages from "../svg/cacheImages";
import {convertToSvg} from "../svg/convertToSvg";
import cleanSvg from "../svg/cleanSvg";
import {makePngFromSvg} from "../svg/makePngFromSvg";
import getBoundingBoxByAngle from "../svg/getBoundingBoxByAngle";
import getModificationData from "../../../services/getModificationData";
import {snapshot} from "valtio";

const isJson = (str) => {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

// loadVariant rebuilds section_id as `${bundleIndex}-${i}@@@${sectionItem.section_id}`
// on every load, but the API-supplied UUID after `@@@` isn't stable (a profile
// change regenerates UUIDs, and sectionData.js strips the prefix entirely on save)
// so saved section_id/sectionId references go stale. We re-attach in two passes:
//   1) index-prefix on the saved element's sectionId (`0-0`, `0-1`)
//   2) fall back to section_name when even the index lineup changed (profile churn)
const indexPrefixOf = (id) => (id || '').split('@@@')[0];

export default async function startModification(modificationData, id) {

	const {sections, elements}  = snapshot(store);

    const modificationDataInIframe = isJson(modificationData?._data) ? {design_data:JSON.parse(modificationData?._data)} : null;
    const designData = modificationDataInIframe ? modificationDataInIframe : await getModificationData(id);


    //console.log("designData", designData, modificationDataInIframe);
    //console.log("modificationData", modificationData);

	const savedSections = designData?.design_data?.sections || [];
	const currentSectionByPrefix = new Map(
		sections.map(s => [indexPrefixOf(s.section_id), s])
	);
	// Key by `${bundle_index}-${section_name}` so a bundle of two products that
	// happen to share a section name (e.g. both have "Front") doesn't collide.
	const bundleNameKey = (bundleIndex, name) => `${bundleIndex ?? 1}::${name}`;
	const currentSectionByBundleName = new Map(
		sections
			.filter(s => s.section_name)
			.map(s => [bundleNameKey(s.bundle_index, s.section_name), s])
	);
	const resolveCurrentSection = (savedSection, element) => {
		const byPrefix = currentSectionByPrefix.get(indexPrefixOf(element?.sectionId));
		if (byPrefix) return byPrefix;
		const savedName = savedSection?.name || savedSection?.section_name;
		if (!savedName) return null;
		return currentSectionByBundleName.get(bundleNameKey(savedSection?.bundle_index, savedName)) || null;
	};

    // processElement (and the unresolved-section guard above) deliberately yield
    // null for elements we can't rebuild — a stale section reference or a failed
    // image conversion. Every consumer of store.elements (mergeImages, ToolBar,
    // ThemeElement, priceCalculatorPerProduct) dereferences `e.sectionId` /
    // `e.elementId`, so any null left in here crashes the whole editor. Drop them.
    store.elements = (await Promise.all(
        savedSections.flatMap(savedSection =>
            savedSection.elements.map(element => {
                const currentSection = resolveCurrentSection(savedSection, element);
                if (!currentSection) return null;
                return processElement(element, currentSection);
            })
        )
    )).filter(Boolean);



	const savedLockedByBundleName = new Map(
		savedSections
			.filter(s => s.name || s.section_name)
			.map(s => [bundleNameKey(s.bundle_index, s.name || s.section_name), !!s.locked])
	);
	store.sections = sections.map(section => (
		{
			...section,
			locked: savedLockedByBundleName.get(bundleNameKey(section.bundle_index, section.section_name)) || false
		}
	))



	const threeViewerSections = window.threeViewer.scene.children.filter(e => e.type === "section");
	threeViewerSections?.forEach(child => child.update());


}

async function processElement(element, section) {
    try {


        let newElement = { ...element, sectionId: section.section_id };

        const image = await fetchAndProcessImage(element.image, element, section);
        if (!image) return null;
        
        await cacheImages(image.blob, element.elementId);
        
        newElement.image = image.url;
        if (element.type === "custom") {
            const rawImage = await fetchAndProcessImage(element.custom.rawImage, element, section);
            if (rawImage) newElement.custom.rawImage = rawImage.url;
        }
        
        return {
            ...newElement,
            ...image.keys,
        };
    } catch (error) {
        console.error("Error processing element:", error);
        return null;
    }
}

async function fetchAndProcessImage(imageUrl, element, section) {
    try {
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error("Network response was not ok.");

        const imageBlob = await response.blob();
        // exportData uploads text/monogram/listTexts artwork as .svg path data;
        // potrace would choke on SVG (expects a raster bitmap), so route SVG sources
        // straight to cleanSvg and rasterize a PNG for the 3D-viewer texture/cache.
        const isSvg = imageBlob.type === 'image/svg+xml' || /\.svg(\?|$)/i.test(imageUrl);

        let svg;
        let pngUrl;
        let pngBlob;
        let svgUrl;

        if (isSvg) {
            svg = await imageBlob.text();
        } else {
            const url = URL.createObjectURL(imageBlob);
            svg = await convertToSvg(url);
            pngUrl = url;
            pngBlob = imageBlob;
        }

        const changedSvg = await cleanSvg(svg, 2000);

        if (isSvg) {
            const pngData = await makePngFromSvg(changedSvg.svg);
            pngUrl = pngData.imgUrl;
            pngBlob = pngData.blob;
            const svgBlob = new Blob([changedSvg.svg], {type: 'image/svg+xml'});
            svgUrl = URL.createObjectURL(svgBlob);
        }

        const snapping = await getBoundingBoxByAngle({
            width: element.width,
            height: element.height,
            angle: element.angle,
            outline: changedSvg.outline,
            offset_x: element.offset_x,
            offset_y: element.offset_Y,
            sectionWidth: section.width,
            sectionHeight: section.height,
            section_offset_y: section.offset_Y,
            lockMove: true,
        });

        return {
            url: pngUrl,
            blob: pngBlob,
            keys: {
                outline: changedSvg.outline,
                snapping,
                visible: true,
                ...(svgUrl ? {svg: svgUrl} : {}),
                insideData: {
                    intersect: true,
                    isInside: true,
                    points: [true, true, true, true],
                }
            },
        };
    } catch (error) {
        console.error("Error fetching and converting the image:", error);
        return null;
    }
}
